import { PushpinFilled, PushpinOutlined } from '@ant-design/icons'
import React, { useCallback } from 'react'
import { Link, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Popover } from 'sutra/components/Popover/Popover'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import {
  ElevationLevelPopover,
  type TierConfig,
} from 'features/trend-detection/components/ElevationLevelPopover'
import { UpdatedBadge } from 'features/trend-detection/components/insight-detail-styles'
import {
  EMERGENCE_BOX_BG,
  EMERGENCE_BOX_BORDER,
  EMERGENCE_COLORS,
  EMERGENCE_ICON_COLOR,
  EMERGENCE_NOTHING_COLOR,
} from 'features/trend-detection/components/issue-constants'
import {
  DetectedIssueIcon,
  EmergenceBarChart,
  EmergenceBarChartForLevel,
  SafetyIndicator,
} from 'features/trend-detection/components/IssueShared'
import { MiniTimeseries } from 'features/trend-detection/components/MiniTimeseries'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import {
  ISSUE_TRACK_TOOLTIP,
  ISSUE_UNTRACK_TOOLTIP,
} from 'features/trend-detection/constants'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentConfigPath,
  trendDetectionIssueDetailPath,
  trendDetectionUpdateDetailPath,
} from 'features/trend-detection/paths'
import type {
  Chart,
  IssueResponse,
  IssueUpdateResponse,
} from 'features/trend-detection/types'
import {
  type EmergenceLevel,
  formatUtcDateFromISO,
  getEmergenceLevel,
} from 'features/trend-detection/utils/helpers'

type ActiveEmergenceLevel = Exclude<EmergenceLevel, 'nothing'>

const TrendLineIcon: React.FC = () => (
  <svg
    width="14"
    height="12"
    viewBox="0 0 20 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <polyline
      points="1,9 5,9 7,3 10,12 13,4 16,9 20,9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EMERGENCE_TIERS: TierConfig<ActiveEmergenceLevel>[] = (
  [
    ['slightly_elevated', 'Slightly elevated'],
    ['elevated', 'Elevated'],
    ['sharply_elevated', 'Sharply elevated'],
  ] as const
).map(([key, label]) => ({
  key,
  label,
  color: EMERGENCE_COLORS[key],
  icon: (active: boolean) => (
    <EmergenceBarChartForLevel level={key} active={active} />
  ),
}))

export interface IssueUpdateCardProps {
  /**
   * Latest IssueUpdate row for this source. `null` is the freshly-tracked case
   * (the user just clicked Track and no scheduled run has produced an update
   * for this source yet). When null the card hides the "Updated …" badge,
   * impact-rate emergence tier, and timeseries chart — those are the visual
   * signal that an update is pending.
   */
  update: IssueUpdateResponse | null
  timeseriesChart: Chart | null
  timeseriesLoading: boolean
  projectTotalChart?: Chart | null
  isShared: boolean
  projectId: string | null
  sourceIssue: IssueResponse
  agentUuid?: string | null
  agentName?: string | null
  onIssueTrackingChange?: (nextTracked: boolean) => void
  trackingLoading?: boolean
}

export const IssueUpdateCard: React.FC<IssueUpdateCardProps> = ({
  update,
  timeseriesChart,
  timeseriesLoading,
  projectTotalChart,
  isShared,
  projectId,
  sourceIssue,
  agentUuid,
  agentName,
  onIssueTrackingChange,
  trackingLoading = false,
}) => {
  // No-update case: scheduled run hasn't produced a re-measurement yet, so
  // fall back to the source issue's most recent measurement and frame the card
  // as the original detection. Card navigates to the source-issue page in
  // this state since there's no update detail page to link to.
  const sourceEmergenceScore =
    sourceIssue.issue_data?.emergence_score_normalized
  const sourcePriorPeriodShare = sourceIssue.issue_data?.prior_period_share
  const currentEmergenceLevel = update
    ? getEmergenceLevel(update.update_data?.impact_rate)
    : getEmergenceLevel(sourceEmergenceScore)
  const popoverShare = update
    ? update.update_data?.share
    : sourceEmergenceScore != null && sourcePriorPeriodShare != null
      ? sourceEmergenceScore * sourcePriorPeriodShare
      : null
  const popoverMultiplier = update
    ? update.update_data?.impact_rate
    : sourceEmergenceScore === null
      ? Infinity
      : typeof sourceEmergenceScore === 'number'
        ? sourceEmergenceScore
        : null
  const isClickable = !isShared && !!projectId

  const navigate = useNavigate()
  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isClickable) return
      if ((e.target as HTMLElement).closest('button, a')) return
      if (update) {
        trackResearchAgents(ResearchAgentsAction.update_detail_click, {
          update_uuid: update.uuid,
          source_issue_uuid: update.source_issue_uuid,
          issue_title: sourceIssue.title,
          is_severe: sourceIssue.is_severe,
          agent_type: ISSUE_DETECTION_AGENT_TYPE,
          agent_uuid: agentUuid,
          agent_name: agentName ?? null,
        })
        navigate(trendDetectionUpdateDetailPath(projectId!, update.uuid))
      } else {
        navigate(trendDetectionIssueDetailPath(projectId!, sourceIssue.uuid))
      }
    },
    [
      isClickable,
      navigate,
      projectId,
      update,
      sourceIssue,
      agentUuid,
      agentName,
    ],
  )

  return (
    <UpdateCardContainer $clickable={isClickable} onClick={handleCardClick}>
      <UpdateCardLayoutRow>
        <UpdateCardMainColumn>
          <UpdateCardHeader>
            <DetectionStatusBadgeRow>
              <DetectionStatusIconBox $level={currentEmergenceLevel}>
                <DetectedIssueIcon
                  color={EMERGENCE_ICON_COLOR[currentEmergenceLevel]}
                  width={24}
                  height={24}
                />
              </DetectionStatusIconBox>
              <UpdateStatusBadgeLabels>
                <DetectionBadgeTitle>
                  {update ? 'Issue Updated' : 'Issue Detected'}
                </DetectionBadgeTitle>
                <EmergenceBadgesWrap>
                  {currentEmergenceLevel !== 'nothing' && (
                    <EmergenceBarChart level={currentEmergenceLevel} />
                  )}
                  <EmergenceBadgeLabel
                    $color={
                      currentEmergenceLevel !== 'nothing'
                        ? EMERGENCE_COLORS[currentEmergenceLevel]
                        : EMERGENCE_NOTHING_COLOR
                    }
                  >
                    {currentEmergenceLevel === 'nothing'
                      ? 'Not currently elevated'
                      : currentEmergenceLevel
                          .replace(/_/g, ' ')
                          .replace(/^\w/, (c) => c.toUpperCase())}
                  </EmergenceBadgeLabel>
                  {currentEmergenceLevel !== 'nothing' && (
                    <ElevationLevelPopover
                      tiers={EMERGENCE_TIERS}
                      activeTierKey={currentEmergenceLevel}
                      share={popoverShare}
                      comparisonMultiplier={popoverMultiplier}
                    />
                  )}
                  {sourceIssue.is_severe && <SafetyIndicator />}
                </EmergenceBadgesWrap>
              </UpdateStatusBadgeLabels>
            </DetectionStatusBadgeRow>
            {!isShared && projectId ? (
              <UpdateCardTitleLink
                to={
                  update
                    ? trendDetectionUpdateDetailPath(projectId, update.uuid)
                    : trendDetectionIssueDetailPath(projectId, sourceIssue.uuid)
                }
                onClick={() => {
                  if (update) {
                    trackResearchAgents(
                      ResearchAgentsAction.update_detail_click,
                      {
                        update_uuid: update.uuid,
                        source_issue_uuid: update.source_issue_uuid,
                        issue_title: sourceIssue.title,
                        is_severe: sourceIssue.is_severe,
                        agent_type: ISSUE_DETECTION_AGENT_TYPE,
                        agent_uuid: agentUuid,
                        agent_name: agentName ?? null,
                      },
                    )
                  }
                }}
              >
                {sourceIssue.title || 'Tracked issue'}
              </UpdateCardTitleLink>
            ) : (
              <UpdateCardTitle>
                {sourceIssue.title || 'Tracked issue'}
              </UpdateCardTitle>
            )}
            {sourceIssue.issue_data?.description ? (
              <UpdateCardDescription>
                {sourceIssue.issue_data.description}
              </UpdateCardDescription>
            ) : null}
          </UpdateCardHeader>
          <UpdateCardFooter>
            <FooterMeta>
              {projectId && agentUuid && agentName ? (
                <FooterAgentLink
                  to={trendDetectionAgentConfigPath(projectId, agentUuid)}
                >
                  {agentName}
                </FooterAgentLink>
              ) : agentName ? (
                <FooterAgentText>{agentName}</FooterAgentText>
              ) : null}
              <Tooltip content="Detection date">
                <FooterTime>
                  · {formatUtcDateFromISO(sourceIssue.create_time)}
                </FooterTime>
              </Tooltip>
              {update && (
                <BadgeWrap>
                  <UpdatedBadge>
                    <TrendLineIcon />
                    Updated {formatUtcDateFromISO(update.create_time)}
                  </UpdatedBadge>
                </BadgeWrap>
              )}
            </FooterMeta>
            <UpdateCardActionsRow>
              {!isShared &&
                !onIssueTrackingChange &&
                sourceIssue.is_tracked && (
                  <PassiveTrackingChip data-testid="passive-tracking-indicator">
                    <TrackingPinIconTracked>
                      <PushpinFilled />
                    </TrackingPinIconTracked>
                    <TrackingStatusText>Tracking</TrackingStatusText>
                  </PassiveTrackingChip>
                )}
              {!isShared &&
                onIssueTrackingChange &&
                (() => {
                  const isTracked = sourceIssue.is_tracked ?? false
                  return (
                    <Popover
                      content={
                        isTracked ? ISSUE_UNTRACK_TOOLTIP : ISSUE_TRACK_TOOLTIP
                      }
                      openOnHover
                      initialFocus={false}
                    >
                      <TrackingToggleButton
                        data-testid="update-tracking-control"
                        variant="secondary"
                        size="xs"
                        loading={trackingLoading}
                        $tracked={isTracked}
                        onClick={() => onIssueTrackingChange(!isTracked)}
                      >
                        {isTracked ? (
                          <TrackingPinIconTracked>
                            <PushpinFilled />
                          </TrackingPinIconTracked>
                        ) : (
                          <TrackingPinIcon>
                            <PushpinOutlined />
                          </TrackingPinIcon>
                        )}
                        <TrackingStatusText>
                          {isTracked ? 'Tracking' : 'Track'}
                        </TrackingStatusText>
                      </TrackingToggleButton>
                    </Popover>
                  )
                })()}
            </UpdateCardActionsRow>
          </UpdateCardFooter>
        </UpdateCardMainColumn>
        <MiniTimeseries
          chart={timeseriesChart}
          loading={timeseriesLoading}
          multiplier={
            update
              ? update.update_data?.impact_rate
              : (popoverMultiplier ?? null)
          }
          recordCount={
            update
              ? (update.update_data?.total_document_count ?? 0)
              : (sourceIssue.issue_data?.total_document_count ?? 0)
          }
          zeroLabel="No mentions this period"
          projectTotalChart={projectTotalChart}
          baselineShare={sourceIssue.issue_data?.prior_period_share}
        />
      </UpdateCardLayoutRow>
    </UpdateCardContainer>
  )
}

const DetectionStatusBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  position: relative;
  z-index: 1;
`

const DetectionStatusIconBox = styled.span<{ $level: EmergenceLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  flex-shrink: 0;
  border-radius: 5px;
  background: ${({ $level }) => EMERGENCE_BOX_BG[$level]};
  border: 0.86px solid ${({ $level }) => EMERGENCE_BOX_BORDER[$level]};
`

const EmergenceBadgesWrap = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmergenceBadgeLabel = styled.span<{ $color: string }>`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-xs, 12px);
  font-weight: 600;
  line-height: var(--Line-height-text-xs, 16px);
  letter-spacing: 0;
  color: ${(p) => p.$color};
`

const UpdateCardContainer = styled.div<{ $clickable?: boolean }>`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${cardBorder};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;

  ${(p) =>
    p.$clickable &&
    `
    cursor: pointer;
    transition: box-shadow 0.2s, border-color 0.2s;

    &:hover {
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
      border-color: ${TREND_DETECTION_PRIMARY_BLUE};
    }
  `}
`

const UpdateCardLayoutRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
`

const UpdateCardMainColumn = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
`

const UpdateCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const UpdateCardTitle = styled.h3`
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${darkBlueFontColor};
  margin: 0;
`

const UpdateCardDescription = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: #868d98;
  margin: 0;
`

const UpdateCardTitleLink = styled(Link)`
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${darkBlueFontColor};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${TREND_DETECTION_PRIMARY_BLUE};
  }
`

const UpdateCardFooter = styled.div`
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const UpdateCardActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const UpdateStatusBadgeLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const DetectionBadgeTitle = styled.span`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-sm, 14px);
  font-weight: 600;
  line-height: var(--Line-height-text-sm, 20px);
  letter-spacing: 0;
  color: ${darkBlueFontColor};
`

const TrackingToggleButton = styled(Button)<{ $tracked: boolean }>`
  padding-inline: 10px;
  border-color: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.28)' : 'rgba(31, 52, 84, 0.16)'};
  border-radius: 999px;
  background: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};

  &&:hover:not(:disabled):not([aria-disabled='true']),
  &&:focus-visible {
    border-color: rgba(79, 114, 164, 0.4);
    color: ${darkBlueFontColor};
    background: ${(p) =>
      p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};
  }
`

const TrackingPinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.72;
`

const TrackingPinIconTracked = styled(TrackingPinIcon)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
`

const TrackingStatusText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${darkBlueFontColor};
  opacity: 0.92;
`

// Passive (non-interactive) "Tracking" pill rendered on read-only feed
// surfaces where the user can't toggle tracking from the card itself —
// preserves the visual signal that the issue is currently tracked.
const PassiveTrackingChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  background: rgba(24, 144, 255, 0.08);
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-size: 12px;
  font-weight: 500;
`

const FooterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1.4;
`

const FooterAgentLink = styled(Link)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const FooterAgentText = styled.span`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
`

const FooterTime = styled.span`
  color: #666;
`

const BadgeWrap = styled.span`
  margin-left: 4px;
`
